import { useTranslations } from "@jugl-web/utils";
import { useMe } from "@web-src/modules/auth/hooks/useMe";
import { appModulesToRequiredUserRoles } from "@jugl-web/domain-resources/common/consts";
import {
  UserRole,
  useRestApiProvider,
  SubscriptionPlanModuleId,
} from "@jugl-web/rest-api";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import featureTasksImage from "./assets/feature-tasks.png";
import featureChatsMeetingsImage from "./assets/feature-chats-meetings.png";
import featureFormsImage from "./assets/feature-forms.png";
import featureInvoicesImage from "./assets/feature-invoices.png";
import featureCatalogImage from "./assets/feature-catalog.png";
import featureWhatsappImage from "./assets/feature-whatsapp.png";
import featureTravelLogImage from "./assets/feature-travel-log.png";
import featureCustomersImage from "./assets/feature-customers.png";
import featureDashboardImage from "./assets/feature-dashboard.png";
import featureDriveImage from "./assets/feature-drive.png";
import featureAIAgentsImage from "./assets/feature-aiagents.png";
import featureIntegrations from "./assets/feature-integrations.png";
import featurePeople from "./assets/feature-people.png";

export type WorkspaceFeature = {
  title: string;
  description: string;
  image: string;
  hasPermission: boolean;
  isLicensed: boolean;
  isEnabled?: boolean;
  isCoreFeature?: boolean;
  isUpcoming?: boolean;
  isNew?: boolean;
  onToggle?: () => void;
};

export const useWorkspaceFeatureCardData = () => {
  const { t } = useTranslations();
  const { me } = useMe();
  const role = me?.role ?? UserRole.member;
  const { entitiesApi } = useRestApiProvider();
  const [toggleModule] = entitiesApi.useUpdateModulesMutation();
  const { subscriptionInfo, entityId } = useEntitySelectedProvider();

  const isModuleEnabled = (module: SubscriptionPlanModuleId) =>
    !!subscriptionInfo.modules?.find(({ id }) => id === module)?.enabled;

  const isModuleLicensed = (module: SubscriptionPlanModuleId) =>
    !!subscriptionInfo.modules?.find(({ id }) => id === module)?.licensed;

  const handleToggleModule = async (moduleId: SubscriptionPlanModuleId) => {
    const modules =
      subscriptionInfo?.modules
        ?.filter(({ enabled }) => enabled)
        .map(({ id }) => id) ?? [];
    const isEnabled = isModuleEnabled(moduleId);
    const newEnabledModules = isEnabled
      ? modules.filter((mod) => mod !== moduleId)
      : [...modules, moduleId];

    const info = modulesData[moduleId];

    await toggleModule({
      entityId,
      data: {
        modules: newEnabledModules.map((item) => item),
      },
      meta: info
        ? {
            successMessage: t(
              {
                id: isEnabled
                  ? "workspace-features-showcase.module-disabled"
                  : "workspace-features-showcase.module-enabled",
                defaultMessage: isEnabled
                  ? "{module} module disabled"
                  : "{module} module enabled",
              },
              {
                module: info.title,
              }
            ),
          }
        : undefined,
    }).unwrap();
  };

  const modulesData: Partial<
    Record<SubscriptionPlanModuleId, WorkspaceFeature>
  > = {
    "Jugl::Task": {
      title: t({
        id: "workspace-features-showcase.tasks.title",
        defaultMessage: "Tasks",
      }),
      description: t({
        id: "workspace-features-showcase.tasks.description",
        defaultMessage:
          "Create, assign, and track tasks. Keep work moving without things slipping through the cracks.",
      }),
      image: featureTasksImage,
      isCoreFeature: true,
      hasPermission: appModulesToRequiredUserRoles.tasks.includes(role),
      isLicensed: isModuleLicensed(SubscriptionPlanModuleId.task),
    },

    "Jugl::Chat": {
      title: t({
        id: "workspace-features-showcase.chats-meetings.title",
        defaultMessage: "Chats & Calls",
      }),
      description: t({
        id: "workspace-features-showcase.chats-meetings.description",
        defaultMessage:
          "Chat with teammates and clients without switching apps. Stay in the loop and keep conversations where the work happens.",
      }),
      image: featureChatsMeetingsImage,
      isCoreFeature: true,
      hasPermission: appModulesToRequiredUserRoles.chat.includes(role),
      isLicensed: isModuleLicensed(SubscriptionPlanModuleId.chat),
    },

    "Jugl::People": {
      title: t({
        id: "workspace-features-showcase.people.title",
        defaultMessage: "People",
      }),
      description: t({
        id: "workspace-features-showcase.people.description",
        defaultMessage:
          "search profiles, assign team tasks, and understand how your org fits together. Less friction, more flow.",
      }),
      image: featurePeople,
      isCoreFeature: true,
      hasPermission: appModulesToRequiredUserRoles.people.includes(role),
      isLicensed: isModuleLicensed(SubscriptionPlanModuleId.people),
    },

    "Jugl::Drive": {
      title: t({
        id: "workspace-features-showcase.drive.title",
        defaultMessage: "Drive",
      }),
      description: t({
        id: "workspace-features-showcase.drive.description",
        defaultMessage:
          "Upload, manage, and share documents across your workspace. All your files in one secure spot.",
      }),
      image: featureDriveImage,
      isEnabled: isModuleEnabled(SubscriptionPlanModuleId.drive),
      hasPermission: appModulesToRequiredUserRoles.drive.includes(role),
      isCoreFeature: true,
      isLicensed: isModuleLicensed(SubscriptionPlanModuleId.drive),
    },

    "Jugl::Clients": {
      title: t({
        id: "workspace-features-showcase.customers.title",
        defaultMessage: "Customers",
      }),
      description: t({
        id: "workspace-features-showcase.customers.description",
        defaultMessage:
          "Store and manage all your client info in one place. Easy to access, even easier to keep updated.",
      }),
      image: featureCustomersImage,
      isEnabled: isModuleEnabled(SubscriptionPlanModuleId.clients),
      hasPermission: appModulesToRequiredUserRoles.clients.includes(role),
      onToggle: () => handleToggleModule(SubscriptionPlanModuleId.clients),
      isLicensed: isModuleLicensed(SubscriptionPlanModuleId.clients),
    },

    "Jugl::OrderForm": {
      title: t({
        id: "workspace-features-showcase.forms.title",
        defaultMessage: "Forms",
      }),
      description: t({
        id: "workspace-features-showcase.forms.description",
        defaultMessage:
          "Create custom forms your clients can fill out to submit orders or requests. No back-and-forth emails needed.",
      }),
      image: featureFormsImage,
      isEnabled: isModuleEnabled(SubscriptionPlanModuleId.orderForm),
      hasPermission: appModulesToRequiredUserRoles.orderForms.includes(role),
      onToggle: () => handleToggleModule(SubscriptionPlanModuleId.orderForm),
      isLicensed: isModuleLicensed(SubscriptionPlanModuleId.orderForm),
    },

    "Jugl::Inventory": {
      title: t({
        id: "workspace-features-showcase.catalog.title",
        defaultMessage: "Catalog",
      }),
      description: t({
        id: "workspace-features-showcase.catalog.description",
        defaultMessage:
          "Manage stock levels, item details, and availability all in one place. Know what’s in, what’s out, and what needs attention.",
      }),
      image: featureCatalogImage,
      isEnabled: isModuleEnabled(SubscriptionPlanModuleId.inventory),
      hasPermission: appModulesToRequiredUserRoles.inventory.includes(role),
      onToggle: () => handleToggleModule(SubscriptionPlanModuleId.inventory),
      isLicensed: isModuleLicensed(SubscriptionPlanModuleId.inventory),
    },

    "Jugl::Invoice": {
      title: t({
        id: "workspace-features-showcase.invoices.title",
        defaultMessage: "Invoices",
      }),
      description: t({
        id: "workspace-features-showcase.invoices.description",
        defaultMessage:
          "Generate professional invoices straight from your orders. No extra steps, no missing details.",
      }),
      image: featureInvoicesImage,
      hasPermission: appModulesToRequiredUserRoles.orderForms.includes(role),
      isNew: true,
      isLicensed: isModuleLicensed(SubscriptionPlanModuleId.invoices),
      onToggle: () => handleToggleModule(SubscriptionPlanModuleId.invoices),
      isEnabled: isModuleEnabled(SubscriptionPlanModuleId.invoices),
    },

    "Jugl::WhatsappCrm": {
      title: t({
        id: "workspace-features-showcase.crm-inbox.title",
        defaultMessage: "CRM Inbox",
      }),
      description: t({
        id: "workspace-features-showcase.whatsApp.description",
        defaultMessage:
          "Automate messages, confirm orders, and share updates to customers directly from your system.",
      }),
      image: featureWhatsappImage,
      isEnabled: isModuleEnabled(SubscriptionPlanModuleId.whatsappCrm),
      hasPermission: appModulesToRequiredUserRoles.waAutomation.includes(role),
      onToggle: () => handleToggleModule(SubscriptionPlanModuleId.whatsappCrm),
      isLicensed: isModuleLicensed(SubscriptionPlanModuleId.whatsappCrm),
    },

    "Jugl::Dashboard": {
      title: t({
        id: "workspace-features-showcase.dashboard.title",
        defaultMessage: "Dashboard",
      }),
      description: t({
        id: "workspace-features-showcase.dashboard.description",
        defaultMessage:
          "See everything that matters at a glance - tasks, deadlines, activity. One screen, total clarity.",
      }),
      image: featureDashboardImage,
      isEnabled: isModuleEnabled(SubscriptionPlanModuleId.dashboard),
      hasPermission: appModulesToRequiredUserRoles.dashboard.includes(role),
      onToggle: () => handleToggleModule(SubscriptionPlanModuleId.dashboard),
      isLicensed: isModuleLicensed(SubscriptionPlanModuleId.dashboard),
    },

    "Jugl::TravelLog": {
      title: t({
        id: "workspace-features-showcase.travelLog.title",
        defaultMessage: "Travel Log",
      }),
      description: t({
        id: "workspace-features-showcase.travelLog.description",
        defaultMessage:
          "Log employee trips with time, location, and task details. Perfect for managing mobile or field teams.",
      }),
      image: featureTravelLogImage,
      isEnabled: isModuleEnabled(SubscriptionPlanModuleId.travelLog),
      hasPermission: appModulesToRequiredUserRoles.travelLog.includes(role),
      onToggle: () => handleToggleModule(SubscriptionPlanModuleId.travelLog),
      isLicensed: isModuleLicensed(SubscriptionPlanModuleId.travelLog),
    },

    "Jugl::Integration": {
      title: t({
        id: "workspace-features-showcase.integrations.title",
        defaultMessage: "Integrations",
      }),
      description: t({
        id: "workspace-features-showcase.integrations.description",
        defaultMessage:
          "Connect your favorite tools in one place. Share data, trigger actions, and create a seamless workflow without switching tabs.",
      }),
      image: featureIntegrations,
      isEnabled: isModuleEnabled(SubscriptionPlanModuleId.integration),
      hasPermission: appModulesToRequiredUserRoles.integrations.includes(role),
      onToggle: () => handleToggleModule(SubscriptionPlanModuleId.integration),
      isLicensed: isModuleLicensed(SubscriptionPlanModuleId.integration),
    },

    "Jugl::AI_Agents": {
      title: t({
        id: "workspace-features-showcase.ai-workflows.title",
        defaultMessage: "AI Workflows",
      }),
      description: t({
        id: "workspace-features-showcase.ai-workflows.description",
        defaultMessage:
          "Your smart assistant for daily ops - create tasks, assess performance, and get instant reports. Less guesswork, more clarity.",
      }),
      image: featureAIAgentsImage,
      isEnabled: isModuleEnabled(SubscriptionPlanModuleId.aiAgents),
      hasPermission: appModulesToRequiredUserRoles.dashboard.includes(role),
      onToggle: () => handleToggleModule(SubscriptionPlanModuleId.aiAgents),
      isLicensed: isModuleLicensed(SubscriptionPlanModuleId.aiAgents),
    },
  };
  return modulesData;
};
